import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { PageHeader, StatCard, Card, Badge } from "@/components/erp/ui";
import { DatePick } from "@/components/erp/DatePick";
import { useAuth } from "@/context/AuthContext";
import { OfficeActivities, OfficeInvites, HelpPanel } from "@/components/erp/DesignExtras";
import { useRoles } from "@/components/erp/RolesManager";
import { HourDebtCard } from "@/components/erp/HourDebtCard";
import { KonsultanToday } from "@/components/erp/KonsultanToday";
import { TeamPrPanel } from "@/components/erp/purchasing/TeamPrPanel";
import { Banknote, FileText, ClipboardList, Users, Boxes, CalendarCheck, UserCheck, FolderKanban, Wallet, PackageCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DrillModal, TrendChart } from "@/components/erp/DashInsights";
import { SatisfactionRecap } from "@/components/erp/SatisfactionRecap";
import { OmsetTarget } from "@/components/erp/OmsetTarget";

const Click = ({ on, id, children }) => <div role="button" tabIndex={0} data-testid={`dash-card-${id}`} onClick={on} onKeyDown={(e) => e.key === "Enter" && on()} className="cursor-pointer transition-transform hover:-translate-y-0.5 [&>*]:h-full" title="Klik untuk rincian">{children}</div>;

const STAGES = ["APPRAISAL","DESIGN","MATERIAL","PRINTING","QC1","FINISHING","QC2","PACKING","DELIVERY"];

export default function Dashboard() {
  const { user } = useAuth();
  const [roleList] = useRoles();
  const [d, setD] = useState(null); const [drill, setDrill] = useState(""); const nav = useNavigate();
  const [period, setPeriod] = useState("week"); const [day, setDay] = useState(new Date(Date.now() + 7 * 36e5).toISOString().slice(0, 10));
  useEffect(() => { api.get("/dashboard", { params: { period, day } }).then(({ data }) => setD(data)).catch(() => {}); }, [period, day]);
  if (!d) return <div className="text-slate-400 font-mono text-sm">Loading…</div>;

  const maxFunnel = Math.max(1, ...Object.values(d.funnel || {}));
  return (
    <div>
      <PageHeader title={`Halo, ${user?.name?.split(" ")[0]} 👋`} subtitle="Ringkasan operasional perusahaan — one data, satu dashboard." />
      <OmsetTarget />
      <HourDebtCard />
      <div className="mb-5"><HelpPanel /></div>
      <TeamPrPanel />
      <KonsultanToday user={user} />
      <SatisfactionRecap />
      <div className="mb-5">{user?.role === "owner" ? <OfficeActivities roles={roleList} /> : <OfficeInvites />}</div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="dash-period">
        {[["day", "Hari ini"], ["week", "Minggu ini"], ["month", "Bulan"], ["year", "Tahun"]].map(([k, l]) => <button key={k} type="button" data-testid={`dash-period-${k}`} onClick={() => setPeriod(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${period === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 bg-white"}`}>{l}</button>)}
        <DatePick size="sm" className="w-40" testid="dash-day" value={day} onChange={(v) => v && setDay(v)} />
        <span className="text-xs text-slate-400">{d.finance?.start}{d.finance?.end !== d.finance?.start ? ` s/d ${d.finance?.end}` : ""}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Click id="invoice" on={() => setDrill("invoices")}><StatCard icon={FileText} label="Invoice terbit" value={formatIDR(d.finance?.invoice_total || 0)} sub={`${d.finance?.invoices || 0} invoice · dibayar ${formatIDR(d.finance?.invoice_paid || 0)}`} accent="text-sky-700" /></Click>
        <Click id="expense" on={() => setDrill("expenses")}><StatCard icon={Wallet} label="Biaya pengeluaran" value={formatIDR(d.finance?.expense_total || 0)} sub={`PO ${formatIDR(d.finance?.expense_lines?.PO || 0)} · finishing ${formatIDR(d.finance?.expense_lines?.FINISHING || 0)} · tim khusus ${formatIDR(d.finance?.expense_lines?.TIM_KHUSUS || 0)} · sales ${formatIDR(d.finance?.expense_lines?.SALES || 0)}`} accent="text-rose-600" /></Click>
        <StatCard icon={Banknote} label="Selisih (invoice − pengeluaran)" value={formatIDR((d.finance?.invoice_total || 0) - (d.finance?.expense_total || 0))} accent="text-emerald-600" />
      </div>
      <TrendChart day={day} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <StatCard icon={Banknote} label="Revenue" value={formatIDR(d.revenue)} accent="text-emerald-600" />
        <Click id="quotations" on={() => setDrill("quotations")}><StatCard icon={FileText} label="Quotations" value={d.quotations} sub={`${d.new_quotations} baru · klik: % per sumber`} /></Click>
        <StatCard icon={ClipboardList} label="Active WO" value={d.active_wo} sub={`${d.completed_wo} selesai`} />
        <Click id="customers" on={() => setDrill("customers")}><StatCard icon={Users} label="Customers" value={d.customers} sub="klik: % per sumber" /></Click>
        <Click id="projects" on={() => nav("/app/projects")}><StatCard icon={FolderKanban} label="Active Projects" value={d.projects} sub="buka Projects" /></Click>
        <Click id="lowstock" on={() => nav("/app/stock")}><StatCard icon={Boxes} label="Low Stock" value={d.low_stock} sub="buka Stok" accent={d.low_stock ? "text-rose-600" : "text-slate-900"} /></Click>
        <StatCard icon={CalendarCheck} label="Booking Hari Ini" value={d.today_bookings} />
        <StatCard icon={UserCheck} label="Hadir Hari Ini" value={d.present_today} sub={`dari ${d.employees} karyawan`} />
        <StatCard icon={Wallet} label="Piutang (Belum Lunas)" value={formatIDR(d.receivables?.total || 0)} sub={`${(d.receivables?.unpaid || 0) + (d.receivables?.partial || 0)} invoice${d.receivables?.overdue ? ` · ${formatIDR(d.receivables.overdue)} jatuh tempo` : ""}`} accent={d.receivables?.overdue ? "text-rose-600" : "text-slate-900"} />
        <StatCard icon={PackageCheck} label="Titip Barang di FOMU" value={d.consignments?.active || 0} sub={d.consignments?.overdue ? `${d.consignments.overdue} unit >30 hari` : "unit dititipkan"} accent={d.consignments?.overdue ? "text-rose-600" : "text-slate-900"} />
      </div>

      {drill && <DrillModal kind={drill} fin={d.finance} onClose={() => setDrill("")} />}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6">
          <h3 className="font-head font-semibold mb-4">Production Funnel</h3>
          <div className="space-y-2.5">
            {STAGES.map((s) => (
              <div key={s} className="flex items-center gap-3">
                <span className="w-24 text-xs font-mono text-slate-500">{s}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                  <div className="bg-sky-500 h-full rounded-full transition-all" style={{ width: `${((d.funnel[s] || 0) / maxFunnel) * 100}%` }} />
                </div>
                <span className="w-6 text-right text-sm font-medium">{d.funnel[s] || 0}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-head font-semibold mb-4">Quotation Terbaru</h3>
          <div className="space-y-3">
            {d.recent_quotations.length === 0 && <p className="text-slate-400 text-sm">Belum ada quotation.</p>}
            {d.recent_quotations.map((q) => (
              <div key={q.id} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0">
                <div>
                  <div className="text-sm font-medium">{q.customer_name}</div>
                  <div className="text-xs font-mono text-slate-400">{q.code}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">{formatIDR(q.quote?.total || 0)}</div>
                  <Badge>{q.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
